import { getPlatform } from '../platform';
import type { DesktopPrinterConfig } from './types';

const LS_KEY = 'zeettu.desktopPrinter.settings';

export type DesktopPrinterSettings = {
  /** Look for Zeettu Print Bridge on this computer. */
  enabled: boolean;
  /** This browser's receipt printer; falls back to the bridge default. */
  preferredPrinterId: string | null;
  /** Local copy of each printer's config (survives bridge reinstall, re-pushed when it comes back). */
  configs: Record<string, DesktopPrinterConfig>;
  /** Printer ids whose local config has not reached the bridge yet. */
  pendingSync: string[];
};

const DEFAULTS: DesktopPrinterSettings = { enabled: true, preferredPrinterId: null, configs: {}, pendingSync: [] };

let cached: DesktopPrinterSettings | null = null;
const listeners = new Set<() => void>();

/** USB / LAN desktop printers need the bridge, which runs on desktop operating systems only. */
export function isDesktopPrintingEligible(): boolean {
  const platform = getPlatform();
  return platform === 'windows' || platform === 'macos' || platform === 'linux';
}

function read(): DesktopPrinterSettings {
  try {
    const raw = globalThis.localStorage?.getItem(LS_KEY);
    if (!raw) return { ...DEFAULTS };
    const j = JSON.parse(raw) as Partial<DesktopPrinterSettings>;
    return {
      enabled: typeof j.enabled === 'boolean' ? j.enabled : DEFAULTS.enabled,
      preferredPrinterId: typeof j.preferredPrinterId === 'string' ? j.preferredPrinterId : null,
      configs: j.configs && typeof j.configs === 'object' ? j.configs : {},
      pendingSync: Array.isArray(j.pendingSync) ? j.pendingSync.filter((id) => typeof id === 'string') : [],
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function getDesktopPrinterSettings(): DesktopPrinterSettings {
  cached ??= read();
  return cached;
}

export function updateDesktopPrinterSettings(
  patch: Partial<DesktopPrinterSettings> | ((current: DesktopPrinterSettings) => Partial<DesktopPrinterSettings>),
): DesktopPrinterSettings {
  const current = getDesktopPrinterSettings();
  cached = { ...current, ...(typeof patch === 'function' ? patch(current) : patch) };
  try {
    globalThis.localStorage?.setItem(LS_KEY, JSON.stringify(cached));
  } catch {
    /* quota */
  }
  listeners.forEach((fn) => fn());
  return cached;
}

export function subscribeDesktopPrinterSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function saveLocalDesktopPrinterConfig(printerId: string, config: DesktopPrinterConfig, synced: boolean): void {
  updateDesktopPrinterSettings((s) => ({
    configs: { ...s.configs, [printerId]: config },
    pendingSync: synced
      ? s.pendingSync.filter((id) => id !== printerId)
      : Array.from(new Set([...s.pendingSync, printerId])),
  }));
}

/** Test helper. */
export function resetDesktopPrinterSettingsCache(): void {
  cached = null;
}
